import React, { useState } from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, ShieldAlert, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import moment from "moment";

const CATEGORY_CONFIG = {
  harassment: { label: "Assetjament laboral", color: "bg-red-100 text-red-700" },
  sexual_harassment: { label: "Assetjament sexual", color: "bg-red-100 text-red-700" },
  labor_conflict: { label: "Conflicte laboral", color: "bg-amber-100 text-amber-700" },
  discrimination: { label: "Discriminació", color: "bg-orange-100 text-orange-700" },
  risk_situation: { label: "Situació de risc", color: "bg-yellow-100 text-yellow-700" },
  other: { label: "Altres", color: "bg-gray-100 text-gray-600" },
};

const STATUS_CONFIG = {
  received: { label: "Rebuda", color: "bg-blue-100 text-blue-700" },
  investigating: { label: "En investigació", color: "bg-amber-100 text-amber-700" },
  resolved: { label: "Resolta", color: "bg-emerald-100 text-emerald-700" },
  closed_no_action: { label: "Tancada sense acció", color: "bg-gray-100 text-gray-600" },
};

const BLANK = {
  category: "harassment", report_date: new Date().toISOString().split("T")[0],
  description: "", status: "received", actions_taken: "", resolution_date: "",
  resolution_notes: "", is_anonymous: false, reporter_name: "",
  assigned_to: "", assigned_email: "", confidential_notes: "",
};

export default function HarassmentCasesList({ items, venueId, onChanged, isAdmin }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [showConfidential, setShowConfidential] = useState({});

  const openForm = (item = null) => {
    setEditing(item);
    setForm(item ? { ...BLANK, ...item } : { ...BLANK, case_number: `CAS-${Date.now().toString().slice(-5)}` });
    setShowForm(true);
  };

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSave = async () => {
    if (!form.description) return;
    setSaving(true);
    if (editing?.id) {
      await hosys.entities.HarassmentCase.update(editing.id, { ...form, venue_id: venueId });
    } else {
      await hosys.entities.HarassmentCase.create({ ...form, venue_id: venueId });
    }
    setSaving(false);
    setShowForm(false);
    onChanged();
  };

  const handleDelete = async (id) => {
    await hosys.entities.HarassmentCase.delete(id);
    onChanged();
  };

  const toggleConfidential = (id) => setShowConfidential(p => ({ ...p, [id]: !p[id] }));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          <Lock className="w-4 h-4" />
          <p className="text-xs font-medium">Registre confidencial · Accés restringit</p>
        </div>
        {isAdmin && (
          <Button onClick={() => openForm()} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
            <Plus className="w-4 h-4" /> Nou cas
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <ShieldAlert className="w-10 h-10 mx-auto mb-2 text-gray-200" />
          <p className="text-sm">Sense comunicacions registrades</p>
          <p className="text-xs text-gray-300 mt-1">Registra qualsevol comunicació o situació de risc de forma confidencial.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const cat = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.other;
            const st = STATUS_CONFIG[item.status] || STATUS_CONFIG.received;
            return (
              <Card key={item.id} className="p-4 border-0 shadow-sm border-l-4 border-l-red-300">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <ShieldAlert className="w-4 h-4 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {item.case_number && <span className="text-xs font-mono text-gray-400">{item.case_number}</span>}
                        <Badge className={`${cat.color} border-0 text-[10px]`}>{cat.label}</Badge>
                        <Badge className={`${st.color} border-0 text-[10px]`}>{st.label}</Badge>
                        {item.is_anonymous && <Badge className="bg-gray-100 text-gray-500 border-0 text-[10px]">Anònim</Badge>}
                      </div>
                      <span className="text-xs text-gray-400">{moment(item.report_date).format("DD/MM/YYYY")}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1 line-clamp-2">{item.description}</p>
                    {item.actions_taken && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        <span className="font-medium">Accions:</span> {item.actions_taken}
                      </p>
                    )}
                    {item.assigned_to && (
                      <p className="text-xs text-gray-400 mt-0.5">Assignat a: {item.assigned_to}</p>
                    )}
                    {item.confidential_notes && isAdmin && (
                      <div className="mt-2">
                        <button onClick={() => toggleConfidential(item.id)} className="flex items-center gap-1 text-[11px] text-amber-700 font-medium">
                          {showConfidential[item.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          Notes confidencials
                        </button>
                        {showConfidential[item.id] && (
                          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-1">{item.confidential_notes}</p>
                        )}
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-[#7B2D3F]" onClick={() => openForm(item)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-red-600" onClick={() => handleDelete(item.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => { if (!o) setShowForm(false); }}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-600" />
              {editing ? "Editar cas" : "Registrar nova comunicació"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
              🔒 Aquest registre és <strong>confidencial</strong>. Només té accés el personal autoritzat.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria *</Label>
                <Select value={form.category} onValueChange={v => set("category", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data de comunicació *</Label>
                <Input type="date" value={form.report_date} onChange={e => set("report_date", e.target.value)} />
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <input type="checkbox" id="anon" checked={form.is_anonymous} onChange={e => set("is_anonymous", e.target.checked)} className="accent-[#7B2D3F]" />
              <Label htmlFor="anon" className="cursor-pointer mb-0 text-sm">Comunicació anònima</Label>
              {!form.is_anonymous && (
                <Input value={form.reporter_name} onChange={e => set("reporter_name", e.target.value)}
                  placeholder="Nom de qui reporta" className="ml-2 h-8 text-sm flex-1" />
              )}
            </div>

            <div>
              <Label>Descripció *</Label>
              <Textarea value={form.description} onChange={e => set("description", e.target.value)} rows={4} placeholder="Descriu els fets de forma objectiva..." />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Estat</Label>
                <Select value={form.status} onValueChange={v => set("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assignat a</Label>
                <Input value={form.assigned_to} onChange={e => set("assigned_to", e.target.value)} placeholder="Responsable del cas" />
              </div>
            </div>

            <div>
              <Label>Accions preses</Label>
              <Textarea value={form.actions_taken} onChange={e => set("actions_taken", e.target.value)} rows={2} placeholder="Mesures adoptades..." />
            </div>

            {(form.status === "resolved" || form.status === "closed_no_action") && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data de resolució</Label>
                  <Input type="date" value={form.resolution_date} onChange={e => set("resolution_date", e.target.value)} />
                </div>
                <div>
                  <Label>Notes de resolució</Label>
                  <Input value={form.resolution_notes} onChange={e => set("resolution_notes", e.target.value)} placeholder="Resultat final..." />
                </div>
              </div>
            )}

            <div>
              <Label className="flex items-center gap-1.5"><Lock className="w-3 h-3 text-amber-600" /> Notes internes confidencials</Label>
              <Textarea value={form.confidential_notes} onChange={e => set("confidential_notes", e.target.value)} rows={2} placeholder="Notes internes (no visibles per a treballadors)..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
            <Button onClick={handleSave} disabled={!form.description || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Actualitzar" : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}